// When the onboarding takeover MUST be on screen — v3 `AccountBootstrapFlow.ts:211-229`.
//
//   · a ready session with no workspace, unless the user is dashboard-only (contract 29 §9: a
//     dashboard-only member legitimately has none) — the fresh signup, which step 1 serves;
//   · a Shopify signup: one workspace (the shop's), a free-access package, and nothing connected
//     but the store (`onboardingEligibility.ts`) — it already has its workspace, so it starts at
//     step 2.
//
// Pure reads of 07 and 12; the host decides what to do with them.

import { computed, type ComputedRef } from 'vue'

import { mustOnboard } from '../service/onboardingSteps'

import {
  FREE_ACCESS_PACKAGE_IDS,
  useAccount,
  useAccountSession,
  useRbac,
  useWorkspace,
} from '@/features/account'
import { useIntegrations } from '@/features/integrations'

export interface OnboardingGate {
  /** Zero workspaces on a ready, non-dashboard-only session. */
  noWorkspace: ComputedRef<boolean>
  /** v3 `shouldShowShopifyOnboarding`. */
  shopify: ComputedRef<boolean>
  /** Either of the two — the takeover is mandatory and cannot be dismissed. */
  required: ComputedRef<boolean>
}

export function useOnboardingGate(): OnboardingGate {
  const session = useAccountSession()
  const account = useAccount()
  const workspace = useWorkspace()
  const rbac = useRbac()
  const { integrations } = useIntegrations()

  const noWorkspace = computed(() =>
    mustOnboard({
      status: session.status.value,
      workspaceCount: workspace.workspaces.value.length,
      onlyDashboard: rbac.onlyDashboard.value,
    }),
  )

  const shopify = computed(() => {
    if (session.status.value !== 'ready' || workspace.workspaces.value.length !== 1) return false
    if (account.accountDetail.value?.user?.is_shopify !== true) return false
    const packageId = account.packageId.value
    if (packageId === null || !FREE_ACCESS_PACKAGE_IDS.has(packageId)) return false
    const connected = Object.entries(integrations.value).filter(([, records]) =>
      Array.isArray(records) ? records.length > 0 : Boolean(records),
    )
    return connected.length === 1 && connected[0]?.[0] === 'shopify_store'
  })

  return { noWorkspace, shopify, required: computed(() => noWorkspace.value || shopify.value) }
}
